import { useCallback } from 'react';
import * as api from '../api';

const normalizeWhatsAppNumber = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length <= 11 && !digits.startsWith('55')) return `55${digits}`;
  return digits;
};

const normalizeTaskPriority = (priority) => {
  const value = String(priority || '').trim().toUpperCase();
  if (!value || value === 'VAZIO') return 'ALTA';
  return value;
};

const areTaskValuesEqual = (current, next) => {
  if (Array.isArray(current) || Array.isArray(next)) {
    const currentList = Array.isArray(current) ? current : [];
    const nextList = Array.isArray(next) ? next : [];
    if (currentList.length !== nextList.length) return false;
    return currentList.every((item, index) => String(item) === String(nextList[index]));
  }
  return String(current ?? '') === String(next ?? '');
};

const buildAssignmentWhatsAppText = ({ member, task, priority, actorName }) => {
  const priorityLabel = normalizeTaskPriority(priority);
  const lines = [
    `${priorityLabel === 'URGENTE' ? '🚨' : '⚠️'} *Nova Tarefa Atribuída*`,
    `Prioridade: *${priorityLabel}*`,
    '',
    `Olá, ${member?.name || member?.initials || 'equipe'}!`,
    `Você foi atribuído(a) à tarefa "${task?.elemento || 'Tarefa'}".`,
    `Status: ${task?.status || 'Sem status'}`,
  ];
  if (task?.entrega) lines.push(`Entrega: ${task.entrega}`);
  lines.push('', `Enviado por ${actorName || 'Gestão Photofine'}.`);
  return lines.join('\n');
};

const buildStatusChangeWhatsAppText = ({ member, taskName, fromStatus, toStatus, priority, actorName }) => {
  const priorityLabel = normalizeTaskPriority(priority);
  const lines = [
    `${priorityLabel === 'URGENTE' ? '🚨' : '⚠️'} *Tarefa Atualizada*`,
    `Prioridade: *${priorityLabel}*`,
    '',
    `Olá, ${member?.name || member?.initials || 'equipe'}!`,
    `A tarefa "${taskName || 'Tarefa'}" foi atualizada para o status *${toStatus || 'Sem status'}* (antes: ${fromStatus || 'Sem status'}).`,
    '',
    `Enviado por ${actorName || 'Gestão Photofine'}.`,
  ];
  return lines.join('\n');
};

export function useTaskActions(saveStateRef, tasks, setTasks, groups, members, currentUser, getBrazilDate, getBrazilDateStr, addNotification, addToast, setReplyingTo, editingUpdateId, setEditingUpdateId, setEditingUpdateText, setActivePanelTaskId, theme) {
  const canCreate = currentUser?.role === 'admin' || theme?.editorCanCreate;
  const shouldTrackActivityLog = theme?.trackActivityLogs !== false;

  const dispatchWhatsAppNotifications = useCallback(async (recipients, buildText, meta) => {
    const list = (recipients || []).filter(Boolean);
    if (list.length === 0) return;

    // Sempre envia para qualquer tipo de membro (admin ou editor).
    // Se algum destinatário não tiver WhatsApp no estado local, tenta buscar
    // a versão atualizada no servidor antes de desistir.
    let sourceMembers = members;
    const hasMissingWhatsApp = list.some((member) => !normalizeWhatsAppNumber(member?.whatsapp));
    if (hasMissingWhatsApp && currentUser?.role === 'admin') {
      try {
        const fresh = await api.fetchTeamMembers();
        if (Array.isArray(fresh) && fresh.length > 0) sourceMembers = fresh;
      } catch (error) {
        // mantém a lista local em caso de falha
      }
    }

    const resolved = list.map((member) => {
      const freshMember = sourceMembers.find((m) => String(m.id) === String(member.id)) || member;
      let whatsapp = normalizeWhatsAppNumber(freshMember?.whatsapp);
      if (!whatsapp && String(freshMember?.id) === String(currentUser?.id)) {
        whatsapp = normalizeWhatsAppNumber(currentUser?.whatsapp);
      }
      return { member: freshMember, whatsapp };
    });

    const valid = resolved.filter((entry) => entry.whatsapp);
    const skippedNames = resolved
      .filter((entry) => !entry.whatsapp)
      .map((entry) => entry.member?.name || entry.member?.initials || 'membro');
    if (skippedNames.length > 0) {
      addToast('WhatsApp', `Sem WhatsApp cadastrado: ${skippedNames.join(', ')}.`, 'warning');
    }
    if (valid.length === 0) return;

    const results = await Promise.allSettled(
      valid.map((entry) => api.sendWhatsAppMessage({
        to: entry.whatsapp,
        text: buildText(entry.member),
        meta,
      }))
    );
    const ok = results.filter((result) => result.status === 'fulfilled').length;
    const failed = results.length - ok;
    if (ok > 0) addToast('WhatsApp', `${ok} notificação(ões) enviada(s) automaticamente.`, 'success');
    if (failed > 0) addToast('WhatsApp', `${failed} notificação(ões) não puderam ser enviadas. Verifique a Evolution API.`, 'error');
  }, [addToast, currentUser, members]);

  const handleAddTask = useCallback((groupId) => {
    if (!canCreate) return;
    const today = getBrazilDateStr();
    // Editor é automaticamente adicionado à equipe para poder ver/editar a tarefa que criou
    const isEditor = currentUser?.role !== 'admin';
    const equipe = isEditor && currentUser?.initials ? [currentUser.initials] : [];
    const newTask = {
      id: tasks.length > 0 ? Math.max(...tasks.map(t => t.id)) + 1 : 1,
      groupId: groupId || groups[0]?.id || 'g1',
      elemento: 'Novo Evento / Ensaio', dataCriacao: today, dataStatus: today, equipe, status: 'VAZIO', contrato: '', observacao: '', extras: '', entrega: '', priority: 'VAZIO', updates: [],
      activityLog: shouldTrackActivityLog ? [{ id: Date.now(), time: 'Agora', authorInitials: currentUser ? currentUser.initials : 'SYS', taskName: 'Novo Evento / Ensaio', field: 'Created', details: 'Grupo: Adicionado' }] : [], files: [],
      createdBy: currentUser?.id || null,
      archived: false,
      archivedAt: null,
      archivedBy: null,
    };
    const newTasks = [...tasks, newTask];
    setTasks(newTasks);
    saveStateRef.current({ tasks: newTasks });
  }, [tasks, setTasks, groups, currentUser, getBrazilDateStr, saveStateRef, canCreate, shouldTrackActivityLog]);

  const handleCreateTaskFromCalendar = useCallback((dateStr) => {
    if (!canCreate) return;
    const today = getBrazilDateStr();
    // Editor é automaticamente adicionado à equipe para poder ver/editar a tarefa que criou
    const isEditor = currentUser?.role !== 'admin';
    const equipe = isEditor && currentUser?.initials ? [currentUser.initials] : [];
    const newTask = {
      id: tasks.length > 0 ? Math.max(...tasks.map(t => t.id)) + 1 : 1,
      groupId: groups[0]?.id || 'g1',
      elemento: 'Novo Evento / Ensaio', dataCriacao: today, dataStatus: today, equipe, status: 'VAZIO', contrato: '', observacao: '', extras: '', entrega: dateStr, priority: 'VAZIO', updates: [],
      activityLog: shouldTrackActivityLog ? [{ id: Date.now(), time: 'Agora', authorInitials: currentUser ? currentUser.initials : 'SYS', taskName: 'Novo Evento / Ensaio', field: 'Created', details: `Data de entrega: ${dateStr}` }] : [], files: [],
      createdBy: currentUser?.id || null,
      archived: false,
      archivedAt: null,
      archivedBy: null,
    };
    const newTasks = [...tasks, newTask];
    setTasks(newTasks);
    saveStateRef.current({ tasks: newTasks });
    setActivePanelTaskId(newTask.id);
  }, [tasks, setTasks, groups, currentUser, getBrazilDateStr, saveStateRef, setActivePanelTaskId, canCreate, shouldTrackActivityLog]);

  const updateTask = useCallback((id, field, value) => {
    let taskBeforeUpdate = null;
    let newMembers = [];
    const adminMembers = members.filter(m => m.role === 'admin' && m.id !== currentUser?.id);
    const notifyAdmins = (payload) => {
      adminMembers.forEach((admin) => {
        addNotification({
          ...payload,
          recipientId: admin.id,
          recipientEmail: admin.email,
          recipientRole: 'admin',
        });
      });
    };

    const newTasks = tasks.map(task => {
      if (task.id === id) {
        if (areTaskValuesEqual(task[field], value)) return task;
        taskBeforeUpdate = task;
        let logDetails = '';
        const nextMembers = Array.isArray(value) ? value : [];
        if (field === 'status' || field === 'priority') logDetails = value;
        else if (field === 'equipe') {
          const currentMembers = task.equipe || [];
          const addedMembers = nextMembers.filter(m => !currentMembers.includes(m));
          const removedMembers = currentMembers.filter(m => !nextMembers.includes(m));
          newMembers = addedMembers;
          const changes = [];
          if (addedMembers.length > 0) changes.push(`Adicionado: ${addedMembers.join(', ')}`);
          if (removedMembers.length > 0) changes.push(`Removido: ${removedMembers.join(', ')}`);
          logDetails = changes.length > 0 ? changes.join(' | ') : 'Equipe atualizada';
        }
        else logDetails = `Alterado para: ${value}`;
        const newLog = { id: Date.now(), time: 'Agora', authorInitials: currentUser ? currentUser.initials : 'SYS', taskName: task.elemento, field: field.charAt(0).toUpperCase() + field.slice(1), details: logDetails };
        let updates = { [field]: value };
        if (field === 'status' && value !== task.status) {
          updates.dataStatus = getBrazilDateStr();
        }
        if (shouldTrackActivityLog) {
          updates.activityLog = [newLog, ...(task.activityLog || [])];
        }
        if (field === 'status' && value === 'FINALIZADO') {
          const today = getBrazilDateStr();
          updates.dataFinalizacao = today;
        }

        return { ...task, ...updates };
      }
      return task;
    });

    if (!taskBeforeUpdate) return;

    // Enviar notificações para novos membros atribuídos
    if (field === 'equipe' && newMembers.length > 0) {
      newMembers.forEach(memberInitials => {
        const member = members.find(m => m.initials === memberInitials);
        if (member && currentUser && member.id !== currentUser.id) {
          addNotification({
            title: 'Nova Tarefa Atribuída',
            message: `Você foi adicionado à tarefa "${taskBeforeUpdate.elemento}" por ${currentUser.name}`,
            type: 'task_assigned',
            eventKey: 'assigned',
            domain: 'tasks',
            priority: 'critical',
            forceBrowser: true,
            requireInteraction: true,
            renotify: true,
            browserTag: `task-assigned-${id}-${member.id}`,
            recipientId: member.id,
            recipientEmail: member.email,
            taskId: id,
            taskName: taskBeforeUpdate.elemento
          });
        }
      });

      const whatsAppRecipients = newMembers
        .map(initials => members.find(m => m.initials === initials))
        .filter(member => member && member.id !== currentUser?.id);
      dispatchWhatsAppNotifications(
        whatsAppRecipients,
        (member) => buildAssignmentWhatsAppText({ member, task: taskBeforeUpdate, priority: taskBeforeUpdate?.priority, actorName: currentUser?.name }),
        { source: 'task_assignment', taskId: id, priority: normalizeTaskPriority(taskBeforeUpdate?.priority) }
      );
    }

    if (taskBeforeUpdate && currentUser) {
      if (field === 'status' && taskBeforeUpdate.status !== value) {
        const statusRecipients = (() => {
          const map = new Map();
          (taskBeforeUpdate.equipe || []).forEach((initials) => {
            const member = members.find(m => m.initials === initials);
            if (member) map.set(String(member.id), member);
          });
          members.filter(m => m.role === 'admin').forEach(member => map.set(String(member.id), member));
          if (currentUser?.id != null) map.delete(String(currentUser.id));
          return [...map.values()];
        })();

        statusRecipients.forEach((member) => {
          addNotification({
            title: 'Status de tarefa atualizado',
            message: `${currentUser.name} alterou "${taskBeforeUpdate.elemento}" de ${taskBeforeUpdate.status || 'Sem status'} para ${value}.`,
            type: 'task_status_changed',
            eventKey: 'taskStatusChanged',
            domain: 'tasks',
            bypassEventFilter: true,
            taskId: id,
            taskName: taskBeforeUpdate.elemento,
            recipientId: member.id,
            recipientEmail: member.email,
          });
        });

        const statusWhatsAppRecipients = (taskBeforeUpdate.equipe || [])
          .map(initials => members.find(m => m.initials === initials))
          .filter(member => member && member.id !== currentUser?.id);
        dispatchWhatsAppNotifications(
          statusWhatsAppRecipients,
          (member) => buildStatusChangeWhatsAppText({
            member,
            taskName: taskBeforeUpdate.elemento,
            fromStatus: taskBeforeUpdate.status,
            toStatus: value,
            priority: taskBeforeUpdate.priority,
            actorName: currentUser.name,
          }),
          { source: 'task_status_change', taskId: id, status: value, priority: normalizeTaskPriority(taskBeforeUpdate.priority) }
        );
        if (value === 'FINALIZADO') {
          notifyAdmins({
            title: 'Tarefa finalizada',
            message: `${currentUser.name} concluiu a tarefa "${taskBeforeUpdate.elemento}".`,
            type: 'task_completed',
            eventKey: 'taskCompleted',
            domain: 'tasks',
            priority: 'critical',
            taskId: id,
            taskName: taskBeforeUpdate.elemento,
          });
        }
      }

      if (field === 'entrega' && taskBeforeUpdate.entrega !== value) {
        notifyAdmins({
          title: 'Prazo de tarefa alterado',
          message: `${currentUser.name} alterou o prazo de "${taskBeforeUpdate.elemento}" para ${value || 'sem data'}.`,
          type: 'task_due_date_changed',
          eventKey: 'taskDueDateChanged',
          domain: 'tasks',
          taskId: id,
          taskName: taskBeforeUpdate.elemento,
        });

        const today = getBrazilDateStr();
        if (value && value < today && taskBeforeUpdate.status !== 'FINALIZADO') {
          notifyAdmins({
            title: 'Tarefa em atraso',
            message: `"${taskBeforeUpdate.elemento}" está com prazo vencido (${value}).`,
            type: 'task_overdue_alert',
            eventKey: 'overdueAlert',
            domain: 'tasks',
            priority: 'critical',
            taskId: id,
            taskName: taskBeforeUpdate.elemento,
          });
        }
      }
    }

    setTasks(newTasks);
    saveStateRef.current({ tasks: newTasks });
  }, [tasks, setTasks, currentUser, getBrazilDateStr, members, addNotification, saveStateRef, shouldTrackActivityLog, dispatchWhatsAppNotifications]);

  const handleAddUpdate = useCallback(async (taskId, text, files = []) => {
    if (!text.trim() && files.length === 0) return;
    const uploadedFiles = [];

    if (files.length > 0) {
      for (const file of files) {
        try {
          const result = await api.uploadFile(taskId, file);
          if (result.file) {
            const newFile = { id: result.file.id, name: result.file.name, size: result.file.size, type: result.file.type, taskId };
            uploadedFiles.push(newFile);
          }
        } catch (err) {
          addToast('Erro no upload', err.message, 'error');
        }
      }
    }

    const now = getBrazilDate();
    const dateStr = `${String(now.getDate()).padStart(2,'0')}/${String(now.getMonth()+1).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
    const newUpdate = { id: Date.now(), text: text, author: currentUser.initials, authorName: currentUser.name, date: dateStr, files: uploadedFiles, likes: [], replies: [] };

    const newTasks = tasks.map(task => {
      if (task.id === taskId) {
        return { ...task, updates: [newUpdate, ...task.updates], files: [...(task.files || []), ...uploadedFiles] };
      }
      return task;
    });
    setTasks(newTasks);
    saveStateRef.current({ tasks: newTasks });

    const currentTask = tasks.find(t => t.id === taskId);
    if (currentTask && currentUser) {
      const adminMembers = members.filter(m => m.role === 'admin');
      adminMembers.forEach((admin) => {
        addNotification({
          title: 'Nota criada em tarefa',
          message: `${currentUser.name} atualizou "${currentTask.elemento}".`,
          type: 'task_update_created',
          eventKey: 'commentAdded',
          domain: 'tasks',
          taskId,
          taskName: currentTask.elemento,
          recipientId: admin.id,
          recipientEmail: admin.email,
        });
      });
    }

    const mentionMatches = [...(text || '').matchAll(/@([A-Za-z0-9_]+)/g)];
    if (mentionMatches.length > 0) {
      const mentionInitials = [...new Set(mentionMatches.map(m => m[1].toUpperCase()))];
      mentionInitials.forEach((mentionedInitials) => {
        const member = members.find(m => (m.initials || '').toUpperCase() === mentionedInitials);
        if (!member || member.id === currentUser?.id) return;
        addNotification({
          title: 'Você foi mencionado',
          message: `${currentUser?.name || 'Equipe'} mencionou você em "${currentTask?.elemento || 'uma tarefa'}".`,
          type: 'mention',
          eventKey: 'mentioned',
          domain: 'tasks',
          taskId,
          taskName: currentTask?.elemento,
          recipientId: member.id,
          recipientEmail: member.email,
        });
      });
    }
  }, [tasks, setTasks, currentUser, getBrazilDate, addToast, saveStateRef, members, addNotification]);

  const handleLikeUpdate = useCallback((taskId, updateId) => {
    const newTasks = tasks.map(task => {
      if (task.id === taskId) {
        const updates = task.updates.map(u => {
          if (u.id === updateId) {
            const likes = u.likes || [];
            const alreadyLiked = likes.includes(currentUser.initials);
            return { ...u, likes: alreadyLiked ? likes.filter(l => l !== currentUser.initials) : [...likes, currentUser.initials] };
          }
          return u;
        });
        return { ...task, updates };
      }
      return task;
    });
    setTasks(newTasks);
    saveStateRef.current({ tasks: newTasks });
  }, [tasks, setTasks, currentUser, saveStateRef]);

  const handleReplyUpdate = useCallback((taskId, updateId, replyText) => {
    if (!replyText.trim()) return;
    const now = getBrazilDate();
    const dateStr = `${String(now.getDate()).padStart(2,'0')}/${String(now.getMonth()+1).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
    const newReply = { id: Date.now(), text: replyText, author: currentUser.initials, authorName: currentUser.name, date: dateStr };
    const newTasks = tasks.map(task => {
      if (task.id === taskId) {
        const updates = task.updates.map(u => {
          if (u.id === updateId) {
            return { ...u, replies: [...(u.replies || []), newReply] };
          }
          return u;
        });
        return { ...task, updates };
      }
      return task;
    });
    setTasks(newTasks);
    saveStateRef.current({ tasks: newTasks });
    setReplyingTo(null);
  }, [tasks, setTasks, currentUser, getBrazilDate, setReplyingTo, saveStateRef]);

  const handleDeleteUpdate = useCallback((taskId, updateId) => {
    const newTasks = tasks.map(task => {
      if (task.id === taskId) {
        return { ...task, updates: task.updates.filter(u => u.id !== updateId) };
      }
      return task;
    });
    setTasks(newTasks);
    saveStateRef.current({ tasks: newTasks });
    if (editingUpdateId === updateId) { setEditingUpdateId(null); setEditingUpdateText(''); }
  }, [tasks, setTasks, editingUpdateId, setEditingUpdateId, setEditingUpdateText, saveStateRef]);

  const handleEditUpdate = useCallback((taskId, updateId, newText) => {
    if (!newText.trim()) return;
    const now = getBrazilDate();
    const dateStr = `${String(now.getDate()).padStart(2,'0')}/${String(now.getMonth()+1).padStart(2,'0')} ${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
    const newTasks = tasks.map(task => {
      if (task.id === taskId) {
        const updates = task.updates.map(u => {
          if (u.id === updateId) {
            return { ...u, text: newText, date: dateStr + ' (editado)' };
          }
          return u;
        });
        return { ...task, updates };
      }
      return task;
    });
    setTasks(newTasks);
    saveStateRef.current({ tasks: newTasks });
    const currentTask = tasks.find(t => t.id === taskId);
    if (currentTask && currentUser) {
      const adminMembers = members.filter(m => m.role === 'admin');
      adminMembers.forEach((admin) => {
        addNotification({
          title: 'Nota editada em tarefa',
          message: `${currentUser.name} editou uma atualização em "${currentTask.elemento}".`,
          type: 'task_update_edited',
          eventKey: 'commentAdded',
          domain: 'tasks',
          taskId,
          taskName: currentTask.elemento,
          recipientId: admin.id,
          recipientEmail: admin.email,
        });
      });
    }
    setEditingUpdateId(null);
    setEditingUpdateText('');
  }, [tasks, setTasks, getBrazilDate, setEditingUpdateId, setEditingUpdateText, saveStateRef, currentUser, members, addNotification]);

  return {
    handleAddTask,
    handleCreateTaskFromCalendar,
    updateTask,
    handleAddUpdate,
    handleLikeUpdate,
    handleReplyUpdate,
    handleDeleteUpdate,
    handleEditUpdate,
  };
}
